// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { TIMEZONE } from '../../../../shared/constants';
import { ProgressLayout } from '../../components/layouts/progress-layout';
import { ManualSection } from '../../components/manual-section';
import { PatchInviteForm } from '../../components/patch-invite-form';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useInviteStore } from '../../store/use-invite-store';

const manualSchema = z.object({
  procedure: z.string().nonempty(),
  date: z.string().nonempty(),
  location: z.string().nonempty(),
});

export const schema = z.object({
  proceduresConfirmed: z.literal(true),
  proceduresManual: z.array(manualSchema),
});

function extractDefaults({ proceduresManual }) {
  return {
    proceduresConfirmed: true,
    proceduresManual: proceduresManual ?? [],
  };
}

function transformValues({ proceduresManual }) {
  return {
    proceduresConfirmed: true,
    proceduresManual,
  };
}

export function History(props) {
  const { invite } = useInviteStore();
  const { t } = useTranslation('page.history');
  const { t: tCommon } = useTranslation();

  const pastProcedureTime = useMemo(() => {
    const date = dayjs(invite.onlineBookingData?.info?.pastProcedureTime).tz(TIMEZONE, false);

    return `${date.format('MMMM')} ${date.format('YYYY')}`;
  }, [invite.onlineBookingData?.info?.pastProcedureTime]);

  return (
    <PatchInviteForm
      nextPath={props.next.path}
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
    >
      <ProgressLayout {...props}>
        <div className="pb-2.5">
          <h1 className="title">{t('title')}</h1>

          {invite.onlineBookingData?.info?.pastProcedureTime &&
          invite.onlineBookingData?.info?.pastProcedure ? (
            <Trans
              i18nKey="text"
              ns="page.history"
              components={{
                p: <p />,
                strong: <strong className="font-semibold" />,
                ul: <ul className="pl-6 list-disc" />,
                li: <li />,
              }}
              values={{
                procedure: tCommon(invite.onlineBookingData?.info?.pastProcedure),
                date: pastProcedureTime,
                company: brand.companyShort,
              }}
            />
          ) : (
            <p>{t('textNoProcedure')}</p>
          )}
        </div>

        <ManualSection
          fieldArrayName="proceduresManual"
          ItemComponent={ManualProcedure}
          showFromButtonLabel={t('addButtonLabel')}
          FormComponent={ManualProcedureForm}
        />
      </ProgressLayout>
    </PatchInviteForm>
  );
}

function ManualProcedure({ item }) {
  const { procedure, date, location } = item;
  return (
    <div className="px-2.5 flex flex-col items-start">
      <h1 className="block mb-2 font-medium">{procedure}</h1>
      <p className="caption">
        <span>{date}</span> • <span>{location}</span>
      </p>
    </div>
  );
}

function ManualProcedureForm({ onAdd }) {
  const { t } = useTranslation('page.history');
  const { append } = useFieldArray({ name: 'proceduresManual' });
  const form = useForm({
    defaultValues: { procedure: '', date: '', location: '' },
    resolver: zodResolver(manualSchema),
  });
  const { isValid } = form.formState;

  return (
    <FormProvider {...form}>
      <div className="space-y-4">
        <Input name="procedure" label={t('procedure')} placeholder={t('procedurePlaceholder')} />
        <Input name="date" label={t('date')} placeholder={t('datePlaceholder')} />
        <Input
          name="location"
          label={t('location')}
          placeholder={t('locationPlaceholder', {
            company: brand.companyShort,
          })}
        />
        <Button
          type="button"
          variant="primary-light"
          onClick={form.handleSubmit((values) => {
            append(values);
            form.reset();
            onAdd();
          })}
          disabled={!isValid}
        >
          {t('saveButton')}
        </Button>
      </div>
    </FormProvider>
  );
}
